import * as React from "react";
import { Info } from "lucide-react";
import { useIsMobile } from "@/hooks/useMediaQuery";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface InfoTooltipProps {
  /** The explanatory content to display */
  content: React.ReactNode;
  /** Additional CSS classes for the trigger button */
  className?: string;
  /** Side alignment for the tooltip/popover */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment for the tooltip/popover */
  align?: "start" | "center" | "end";
}

/**
 * InfoTooltip - A reusable component for displaying explanatory tooltips
 *
 * Behavior:
 * - Desktop: Hover over (i) icon to show tooltip
 * - Mobile: Click/tap (i) icon to show popover (dismissible)
 * - Dark/Light mode: Automatically adapts to current theme
 *
 * Usage:
 * ```tsx
 * <InfoTooltip content="This metric shows the total solar energy generated" />
 * ```
 */
export function InfoTooltip({
  content,
  className,
  side = "top",
  align = "center",
}: InfoTooltipProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  // Icon button shared between tooltip and popover
  const triggerButton = (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-full",
        "text-muted-foreground hover:text-foreground transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-[#8FB560] focus:ring-offset-2",
        className
      )}
      aria-label="More information"
    >
      <Info className="h-3.5 w-3.5" />
    </button>
  );

  // Mobile: Use Popover for click interaction
  if (isMobile) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {triggerButton}
        </PopoverTrigger>
        <PopoverContent
          side={side}
          align={align}
          className="w-[280px] p-3 text-xs text-foreground bg-card border-border"
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  // Desktop: Use Tooltip for hover interaction
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {triggerButton}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className="max-w-[280px] text-xs bg-popover text-popover-foreground border-border p-3"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
